import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  TrendingUp, TrendingDown, Receipt, ShoppingCart, Users, Package, Briefcase,
  LifeBuoy, Wallet, ArrowUpRight, ClipboardCheck, AlertTriangle,
  Sofa, Cpu, HardDrive, ClipboardList, Truck, Sparkles, ListChecks,
  Landmark, Smartphone, Banknote, Scale, PiggyBank,
} from "lucide-react";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis,
  Tooltip, CartesianGrid, AreaChart, Area, PieChart, Pie, Cell, Legend,
} from "recharts";

const fmt = (n) => new Intl.NumberFormat("en-TZ").format(Math.round(n || 0));
const fmtTZS = (n) => `TZS ${fmt(n)}`;

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = 0;
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(from + (target - from) * eased);
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return value;
}

function DeltaBadge({ delta, goodWhenUp = true }) {
  if (delta === null || delta === undefined || !isFinite(delta)) return null;
  const isUp = delta >= 0;
  const isGood = isUp === goodWhenUp;
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <Badge variant="outline" className={`text-[10px] gap-1 ${isGood ? "border-emerald-500/30 text-emerald-500" : "border-rose-500/30 text-rose-500"}`}>
      <Icon className="w-3 h-3" />{Math.abs(delta).toFixed(0)}% vs last month
    </Badge>
  );
}

function KpiCard({ icon: Icon, label, value, hint, accent = "blue", to }) {
  const accentMap = {
    blue: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
    teal: "bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/20",
    rose: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20",
  };
  const inner = (
    <Card className="kn-card relative overflow-hidden p-6 border border-border bg-card rounded-xl h-full">
      <div className="flex items-start justify-between gap-2">
        <div className={`w-10 h-10 rounded-lg border ${accentMap[accent]} flex items-center justify-center shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        {hint}
      </div>
      <div className="mt-5">
        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
        <p className="font-display text-2xl font-semibold mt-1">{value}</p>
      </div>
    </Card>
  );
  return to ? <Link to={to} className="block h-full">{inner}</Link> : inner;
}

function MiniStat({ label, value, icon: Icon, to }) {
  const inner = (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card kn-card h-full">
      <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
        <p className="font-display text-lg font-semibold">{value ?? 0}</p>
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}

const attentionToneMap = {
  rose: "border-rose-500/25 bg-rose-500/5 text-rose-600 dark:text-rose-400 hover:bg-rose-500/10",
  amber: "border-amber-500/25 bg-amber-500/5 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10",
  blue: "border-blue-500/25 bg-blue-500/5 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10",
};

const ACCOUNT_ICONS = {
  "NMB Bank": Landmark, "Equity Bank": Landmark,
  "Selcom Biz": Smartphone, "Mixx by Yas": Smartphone,
  "Petty Cash": Banknote, "Accounts Receivable": Wallet, "Accounts Payable": Scale,
  "Owner's Equity": PiggyBank, "Sales Revenue": TrendingUp, "Operating Expenses": TrendingDown,
};

function greetingFor(hour) {
  if (hour < 5) return "Still up";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const { user, canViewPage } = useAuth();
  const [data, setData] = useState(null);
  // null = the viewer can't see work orders (403) -- the panel is hidden.
  const [workOrders, setWorkOrders] = useState(null);
  const [approvalsStats, setApprovalsStats] = useState({ my_pending: 0 });
  const [accounts, setAccounts] = useState(null); // null = not permitted / not loaded yet
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/summary").then((r) => r.data),
      api.get("/dashboard/work-orders-overview").then((r) => r.data).catch(() => null),
      api.get("/approvals/stats").then((r) => r.data).catch(() => ({ my_pending: 0 })),
      // Chart of accounts is finance-role-gated (H2) -- a Technician etc.
      // simply won't see this panel, which is correct, not a bug.
      api.get("/chart-of-accounts/").then((r) => r.data.items).catch(() => null),
      // Sales with a due_date (partial payment) or next_due_date (recurring
      // product, e.g. a monthly internet package) -- combined into one
      // soonest-first "what's coming up" list.
      api.get("/sales-orders/", { params: { limit: 500 } }).then((r) => r.data.items).catch(() => []),
      api.get("/dashboard/invoice-summary").then((r) => r.data.months).catch(() => []),
    ]).then(([s, w, a, coa, sales, invSummary]) => {
      setData(s); setWorkOrders(w); setApprovalsStats(a); setAccounts(coa);
      setInvoiceSummary(invSummary || []);
      const upcoming = (sales || [])
        .map((sale) => ({
          id: sale.id, customer: sale.customer, amount: sale.amount,
          dueDate: sale.payment_status === "partial" ? sale.due_date : sale.next_due_date,
          reason: sale.payment_status === "partial" ? "Balance due" : "Recurring renewal",
        }))
        .filter((p) => p.dueDate)
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
        .slice(0, 8);
      setUpcomingPayments(upcoming);
    }).finally(() => setLoading(false));
  }, []);

  const animatedProfit = useCountUp(data?.kpis?.profit || 0);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  const k = data?.kpis || {};
  const c = data?.counts || {};
  const trend = (data?.trend || []).map((t) => ({ ...t, label: t.month?.slice(2) }));

  let revenueDelta = null, expenseDelta = null, profitDelta = null;
  if (trend.length >= 2) {
    const prev = trend[trend.length - 2];
    const curr = trend[trend.length - 1];
    if (prev.revenue > 0) revenueDelta = ((curr.revenue - prev.revenue) / prev.revenue) * 100;
    if (prev.expenses > 0) expenseDelta = ((curr.expenses - prev.expenses) / prev.expenses) * 100;
    const prevProfit = prev.revenue - prev.expenses;
    if (prevProfit !== 0) profitDelta = (((curr.revenue - curr.expenses) - prevProfit) / Math.abs(prevProfit)) * 100;
  }

  const pieData = [
    { name: "Receivables", value: k.pending_revenue, color: "hsl(var(--primary))" },
    { name: "Expenses", value: k.expenses, color: "hsl(var(--accent))" },
    { name: "Revenue", value: k.revenue, color: "#10B981" },
  ].filter((d) => d.value > 0);

  const attentionItems = [];
  if (workOrders?.overdue_count > 0) {
    attentionItems.push({
      key: "overdue-wo", icon: AlertTriangle, tone: "rose",
      text: `${workOrders.overdue_count} work order${workOrders.overdue_count > 1 ? "s" : ""} overdue`,
      to: "/service/work-orders",
    });
  }
  if (approvalsStats.my_pending > 0) {
    attentionItems.push({
      key: "pending-approvals", icon: ListChecks, tone: "amber",
      text: `${approvalsStats.my_pending} approval${approvalsStats.my_pending > 1 ? "s" : ""} waiting on you`,
      to: "/approvals",
    });
  }
  if ((k.pending_revenue || 0) > 0) {
    attentionItems.push({
      key: "receivables", icon: Wallet, tone: "blue",
      text: `${fmtTZS(k.pending_revenue)} outstanding from customers`,
      to: "/creditors-debtors",
    });
  }

  const firstName = (user?.name || user?.email || "there").split(" ")[0];
  const greeting = greetingFor(new Date().getHours());

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <motion.div
        initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4 }}
        className="flex flex-wrap items-end justify-between gap-4"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-semibold">Dashboard</p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-1">
            {greeting}, {firstName}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Here's how KaribuNet Limited is doing today.</p>
        </div>
        <Badge className="kn-gradient-bg text-white border-0 px-3 py-1.5 text-xs">
          {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </Badge>
      </motion.div>

      {/* Needs your attention — surfaces cross-module action items in one place */}
      <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.05 }}>
        <Card className="p-5 rounded-xl border border-border bg-card" data-testid="attention-panel">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-display text-base font-semibold">Needs your attention</h3>
          </div>
          {attentionItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing urgent — everything's on track.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {attentionItems.map((a) => (
                <Link
                  key={a.key}
                  to={a.to}
                  data-testid={`attention-${a.key}`}
                  className={`flex items-center gap-2.5 px-4 py-3 rounded-lg border transition-all hover:translate-y-[-1px] ${attentionToneMap[a.tone]}`}
                >
                  <a.icon className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-medium">{a.text}</span>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </motion.div>

      {/* Upcoming Payments — partial-payment balances and recurring renewals
          (e.g. monthly internet packages), soonest due first */}
      {upcomingPayments.length > 0 && (
        <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.08 }}>
          <Card className="p-5 rounded-xl border border-border bg-card" data-testid="upcoming-payments-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-primary" />
                <h3 className="font-display text-base font-semibold">Upcoming Payments</h3>
              </div>
              <Link to="/crm/sales-orders" className="text-xs text-primary hover:underline">View all sales →</Link>
            </div>
            <div className="space-y-2">
              {upcomingPayments.map((p) => {
                const days = Math.ceil((new Date(p.dueDate) - new Date()) / 86400000);
                const overdue = days < 0;
                return (
                  <Link
                    key={p.id}
                    to="/crm/sales-orders"
                    data-testid={`upcoming-payment-${p.id}`}
                    className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-all hover:translate-y-[-1px] ${
                      overdue ? "border-rose-500/30 bg-rose-500/5" : "border-border bg-background"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{p.customer}</p>
                      <p className="text-xs text-muted-foreground">{p.reason}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-display font-semibold">{fmtTZS(p.amount)}</p>
                      <p className={`text-[10px] ${overdue ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>
                        {overdue ? `${Math.abs(days)}d overdue` : days === 0 ? "Due today" : `Due in ${days}d · ${p.dueDate}`}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </Card>
        </motion.div>
      )}

      {/* Hero — Net Profit banner with inline trend sparkline. The backend
          only sends a figure to users allowed to see its source modules;
          a missing figure hides its card instead of showing a fake 0. */}
      {k.profit !== undefined && (
      <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.1 }}>
        <div className="kn-gradient-border relative p-7 sm:p-8 rounded-2xl border border-border bg-card overflow-hidden" data-testid="net-profit-hero">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gradient-to-br from-blue-500/20 to-amber-500/20 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-3">
                <p className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Net Profit — this period</p>
                <DeltaBadge delta={profitDelta} goodWhenUp />
              </div>
              <p className="font-display text-4xl sm:text-5xl font-bold mt-3 kn-gradient-text">
                {fmtTZS(animatedProfit)}
              </p>
              <p className="text-xs text-muted-foreground mt-2">Revenue received minus approved expenses</p>
              <Link to="/reports" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-4">
                View full report <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            {trend.length > 1 && (
              <div className="w-full lg:w-64 h-20 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend}>
                    <defs>
                      <linearGradient id="gHero" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" fill="url(#gHero)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </motion.div>
      )}

      {/* Accounts Overview -- finance-role-gated (H2); simply absent for other roles */}
      {accounts && accounts.length > 0 && (
        <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.15 }}>
          <Card className="p-6 rounded-xl border border-border bg-card" data-testid="accounts-overview-panel">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
                  <Landmark className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold">Accounts Overview</h3>
                  <p className="text-xs text-muted-foreground">Chart of accounts &amp; real balances</p>
                </div>
              </div>
              <Link to="/accounting/chart" className="text-xs text-primary hover:underline">Manage →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {accounts.map((acc) => {
                const Icon = ACCOUNT_ICONS[acc.name] || Wallet;
                const isZeroPlaceholder = acc.balance === 0 && acc.type === "Asset" && acc.notes;
                return (
                  <Link
                    key={acc.id}
                    to={`/accounting/statement/${encodeURIComponent(acc.name)}`}
                    className="p-4 rounded-lg border border-border bg-background hover:border-primary/40 hover:translate-y-[-1px] transition-all block"
                    title={acc.notes || ""}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground truncate">{acc.name}</p>
                    </div>
                    <p className="font-display text-base font-semibold truncate">
                      {isZeroPlaceholder ? "—" : fmtTZS(acc.balance)}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {isZeroPlaceholder ? "Awaiting your statement" : acc.type}
                    </p>
                  </Link>
                );
              })}
            </div>
          </Card>
        </motion.div>
      )}

      {/* Secondary KPI cards */}
      <motion.div
        initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        {k.revenue !== undefined && <KpiCard icon={Receipt} label="Revenue" value={fmtTZS(k.revenue)} hint={<DeltaBadge delta={revenueDelta} goodWhenUp />} accent="blue" to="/crm/invoices" />}
        {k.pending_revenue !== undefined && <KpiCard icon={Wallet} label="Outstanding Receivables" value={fmtTZS(k.pending_revenue)} accent="amber" to="/creditors-debtors" />}
        {k.expenses !== undefined && <KpiCard icon={TrendingDown} label="Expenses" value={fmtTZS(k.expenses)} hint={<DeltaBadge delta={expenseDelta} goodWhenUp={false} />} accent="rose" to="/accounting/expenses" />}
      </motion.div>

      {/* Monthly Invoice Summary -- real (non-proforma) invoices only, grouped
          by issue month: how many went out and their current paid/partial/
          unpaid status. */}
      {invoiceSummary.length > 0 && (
        <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.4, delay: 0.22 }}>
          <Card className="p-6 rounded-xl border border-border bg-card" data-testid="invoice-summary-panel">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
                <Receipt className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold">Monthly Invoice Summary</h3>
                <p className="text-xs text-muted-foreground">Tax invoices issued each month and their current payment status</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Sent</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Partial</TableHead>
                    <TableHead className="text-right">Unpaid</TableHead>
                    <TableHead className="text-right">Billed</TableHead>
                    <TableHead className="text-right">Collected</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoiceSummary.map((m) => (
                    <TableRow key={m.month}>
                      <TableCell className="font-medium">
                        {new Date(`${m.month}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                      </TableCell>
                      <TableCell className="text-right">{m.sent}</TableCell>
                      <TableCell className="text-right">
                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">{m.paid}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-amber-600 dark:text-amber-400 font-medium">{m.partially_paid}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-rose-600 dark:text-rose-400 font-medium">{m.unpaid}</span>
                      </TableCell>
                      <TableCell className="text-right">{fmtTZS(m.billed_amount)}</TableCell>
                      <TableCell className="text-right">{fmtTZS(m.collected_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Work Orders / Pending Works - anyone who can view work orders */}
      {workOrders && (
      <Card className="p-6 rounded-xl border border-border bg-card" data-testid="work-orders-panel">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
              <ClipboardCheck className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">Pending Work Orders</h3>
              <p className="text-xs text-muted-foreground">All open & scheduled field works</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {workOrders.overdue_count > 0 && (
              <Badge variant="outline" className="border-rose-500/40 text-rose-600 bg-rose-500/5 gap-1">
                <AlertTriangle className="w-3 h-3" /> {workOrders.overdue_count} overdue
              </Badge>
            )}
            <Link to="/service/work-orders" className="text-xs text-primary hover:underline">View all →</Link>
          </div>
        </div>

        {workOrders.items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No open work orders</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {workOrders.items.slice(0, 6).map((w) => (
              <Link
                key={w.id}
                to="/service/work-orders"
                data-testid={`work-order-${w.id}`}
                className={`flex items-center justify-between gap-3 p-4 rounded-lg border transition-all hover:translate-y-[-1px] hover:shadow-md ${
                  w.overdue
                    ? "border-rose-500/30 bg-rose-500/5"
                    : "border-border bg-background"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] text-muted-foreground">{w.wo_no}</span>
                    {w.overdue && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-600 font-semibold flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {w.days_late}d late
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium truncate">{w.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {w.customer || "—"} · {w.technician || "Unassigned"}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Due</p>
                  <p className={`text-sm font-display font-semibold ${w.overdue ? "text-rose-600" : ""}`}>{w.due_date || "—"}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
      )}

      {/* Charts -- need both revenue and expense figures */}
      {k.profit !== undefined && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 rounded-xl border border-border bg-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-semibold">Revenue vs Expenses</h3>
              <p className="text-xs text-muted-foreground">6-month rolling view (TZS)</p>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} formatter={(v) => fmtTZS(v)} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" fill="url(#gRev)" strokeWidth={2} />
                <Area type="monotone" dataKey="expenses" stroke="hsl(var(--accent))" fill="url(#gExp)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-xl border border-border bg-card">
          <h3 className="font-display text-lg font-semibold">Cash Distribution</h3>
          <p className="text-xs text-muted-foreground">Inflow vs outflow</p>
          <div className="h-64 mt-2">
            {pieData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={4}>
                    {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtTZS(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
      )}

      {/* Mini stats - ALL CLICKABLE; only modules the viewer can open */}
      <div>
        <h3 className="font-display text-lg font-semibold mb-3">Quick access</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {c.customers !== undefined && <MiniStat label="Customers" value={c.customers} icon={Users} to="/crm/customers" />}
          {c.leads !== undefined && <MiniStat label="Leads" value={c.leads} icon={Briefcase} to="/crm/leads" />}
          {c.invoices !== undefined && <MiniStat label="Invoices" value={c.invoices} icon={Receipt} to="/crm/invoices" />}
          {c.sales_orders !== undefined && <MiniStat label="Sales" value={c.sales_orders} icon={ClipboardList} to="/crm/sales-orders" />}
          {c.suppliers !== undefined && <MiniStat label="Suppliers" value={c.suppliers} icon={Truck} to="/purchases/suppliers" />}
          {c.purchase_orders !== undefined && <MiniStat label="Purchase Orders" value={c.purchase_orders} icon={ShoppingCart} to="/purchases/orders" />}
          {c.products !== undefined && <MiniStat label="Products" value={c.products} icon={Package} to="/price-list" />}
          {c.office_items !== undefined && <MiniStat label="Office Items" value={c.office_items} icon={Sofa} to="/inventory/office-items" />}
          {c.equipments !== undefined && <MiniStat label="Equipments" value={c.equipments} icon={Cpu} to="/inventory/equipments" />}
          {c.assets !== undefined && <MiniStat label="Assets" value={c.assets} icon={HardDrive} to="/assets/register" />}
          {c.employees !== undefined && <MiniStat label="Employees" value={c.employees} icon={Users} to="/hrm/employees" />}
          {c.tickets !== undefined && <MiniStat label="Open Tickets" value={c.tickets} icon={LifeBuoy} to="/service/tickets" />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {k.revenue !== undefined && (
        <Card className="p-6 rounded-xl border border-border bg-card">
          <h3 className="font-display text-lg font-semibold">Top Customers</h3>
          <p className="text-xs text-muted-foreground mb-4">By invoice value</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.top_customers || []} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid stroke="hsl(var(--border))" horizontal={false} />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
                <YAxis type="category" dataKey="customer" stroke="hsl(var(--muted-foreground))" fontSize={10} width={120} />
                <Tooltip formatter={(v) => fmtTZS(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Bar dataKey="total" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        )}

        {canViewPage("audit_logs") && (
        <Card className="p-6 rounded-xl border border-border bg-card">
          <h3 className="font-display text-lg font-semibold">Recent Activity</h3>
          <p className="text-xs text-muted-foreground mb-3">Audit trail</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(data?.recent_activities || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No activity yet</p>
            ) : data.recent_activities.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                <div className="flex-1 text-sm">
                  <span className="font-medium">{a.email}</span>
                  <span className="text-muted-foreground"> · {a.action}</span>
                </div>
                <span className="text-[10px] text-muted-foreground">{a.ts?.slice(11, 16)}</span>
              </div>
            ))}
          </div>
        </Card>
        )}
      </div>
    </div>
  );
}
